import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Building2, ArrowLeft, MapPin, Users, Ruler, DollarSign, FileText, Database, ExternalLink, Layers } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import EmptyState from '../components/shared/EmptyState';
import ErrorState from '../components/shared/ErrorState';
import { useBusinessUnit } from '../hooks/useBusinessUnit';
import { supabase } from '../supabaseClient';
import { USE_META, PRODUCT_LABEL, MIXED_BADGE, REVIEW_BADGE, FLEX_BADGE } from '../components/launches/useCategoryMeta';
import UnidadesSection from '../components/launches/UnidadesSection';

const PHASE_META = {
  pre_launch:   { label: 'Pré-lançamento', color: 'bg-purple-100 text-purple-700' },
  launch:       { label: 'Lançamento',     color: 'bg-emerald-100 text-emerald-700' },
  construction: { label: 'Construção',     color: 'bg-amber-100 text-amber-700' },
  delivered:    { label: 'Entregue',       color: 'bg-blue-100 text-blue-700' },
};

const STATUS_META = {
  active:    { label: 'Ativo',     color: 'bg-emerald-50 text-emerald-700' },
  inactive:  { label: 'Inativo',   color: 'bg-slate-100 text-slate-600' },
  suspended: { label: 'Suspenso',  color: 'bg-amber-50 text-amber-700' },
  cancelled: { label: 'Cancelado', color: 'bg-red-50 text-red-700' },
};

function fmtBRL(v, { maxFrac = 0 } = {}) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: maxFrac });
}

function fmtDate(d) {
  if (!d) return '—';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
}

function fmtNum(v) {
  if (v == null) return '—';
  const n = Number(v);
  return isFinite(n) ? n.toLocaleString('pt-BR') : '—';
}

function fmtPct(v) {
  if (v == null) return '—';
  const n = Number(v);
  return isFinite(n) ? `${n}%` : '—';
}

function fmtRange(a, b, formatter) {
  const fa = a != null ? formatter(a) : null;
  const fb = b != null ? formatter(b) : null;
  if (!fa && !fb) return '—';
  if (fa && fb && fa !== fb) return `${fa} – ${fb}`;
  return fa || fb;
}

export default function LaunchEmpreendimentoDetailPage() {
  const { availableUnits, isLoading: buLoading } = useBusinessUnit();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [tipologias, setTipologias] = useState([]);
  const [incorporadoras, setIncorporadoras] = useState([]);
  const [precos, setPrecos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (buLoading || !availableUnits?.includes('launches') || !id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const [empRes, tipRes, incRes, precRes, matRes] = await Promise.all([
        supabase.from('empreendimentos').select('*').eq('id', id).maybeSingle(),
        supabase.from('empreendimento_tipologias').select('*').eq('empreendimento_id', id).order('tipo'),
        supabase
          .from('empreendimento_incorporadoras')
          .select('id, role, participation_pct, is_lead, empresa:empresas(id, empresa, company_name)')
          .eq('empreendimento_id', id),
        supabase
          .from('tabelas_preco')
          .select('*, tipologia:empreendimento_tipologias(id, tipo, label)')
          .eq('empreendimento_id', id)
          .is('valid_until', null)
          .is('unidade_id', null)
          .order('valid_from', { ascending: false }),
        supabase
          .from('empreendimento_materials')
          .select('id, material_type, source_url, source_type, version_label, original_filename, is_current, is_featured, display_order')
          .eq('empreendimento_id', id)
          .eq('is_current', true)
          .order('display_order'),
      ]);
      if (cancelled) return;
      if (empRes.error) { setError(empRes.error.message); setLoading(false); return; }
      setData(empRes.data || null);
      setTipologias(tipRes.data || []);
      setIncorporadoras(incRes.data || []);
      setPrecos(precRes.data || []);
      setMaterials(matRes.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, buLoading, availableUnits]);

  if (buLoading) {
    return (
      <>
        <AppHeader title="Empreendimento" icon={Building2} />
        <div className="p-6"><LoadingSpinner /></div>
      </>
    );
  }
  if (!availableUnits || !availableUnits.includes('launches')) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <>
        <AppHeader title="Empreendimento" icon={Building2} />
        <div className="p-6"><LoadingSpinner /></div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <AppHeader title="Empreendimento" icon={Building2} />
        <div className="p-6"><ErrorState message={error} /></div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <AppHeader title="Empreendimento" icon={Building2} />
        <div className="p-6">
          <EmptyState icon={Building2} message="Empreendimento não encontrado" />
          <div className="text-center mt-3">
            <Link to="/launches/empreendimentos" className="text-xs text-violet-700 hover:underline">← voltar ao catálogo</Link>
          </div>
        </div>
      </>
    );
  }

  const phaseMeta = PHASE_META[data.phase] || { label: data.phase, color: 'bg-slate-100 text-slate-700' };
  const statusMeta = STATUS_META[data.status] || { label: data.status, color: 'bg-slate-100 text-slate-600' };
  const primaryUseMeta = USE_META[data.primary_use_category] ?? USE_META.unknown;
  const distinctTipUses = Array.from(new Set(tipologias.map((t) => t.use_category).filter(Boolean)));
  const headerNeedsReview = data.use_classification_status === 'needs_review';
  const addressLine = [data.address_street, data.address_number].filter(Boolean).join(', ');
  const localityLine = [data.neighborhood, data.city, data.state].filter(Boolean).join(' · ');

  return (
    <>
      <AppHeader title={data.name || 'Empreendimento'} subtitle={data.short_name || data.slug || ''} icon={Building2} />
      <div className="p-4 lg:p-6 space-y-4 max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/launches/empreendimentos" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
            <ArrowLeft size={11} /> voltar ao catálogo
          </Link>
          <div className="flex flex-wrap items-center gap-1.5 justify-end">
            <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${phaseMeta.color}`}>{phaseMeta.label}</span>
            <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${statusMeta.color}`}>{statusMeta.label}</span>
            <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${primaryUseMeta.color}`}>{primaryUseMeta.label}</span>
            {distinctTipUses.length > 1 && (
              <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${MIXED_BADGE}`}>Misto</span>
            )}
            {headerNeedsReview && (
              <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${REVIEW_BADGE}`}>Revisar</span>
            )}
          </div>
        </div>

        <section className="bg-white border border-slate-200 rounded p-4">
          <div className="flex items-start gap-2 text-sm text-slate-700">
            <MapPin size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              {addressLine && <div>{addressLine}</div>}
              {localityLine && <div className="text-xs text-slate-500">{localityLine}</div>}
              {data.zip_code && <div className="text-[11px] text-slate-400">CEP {data.zip_code}</div>}
              {!addressLine && !localityLine && <span className="text-slate-400">Endereço não cadastrado</span>}
            </div>
          </div>
          {(data.launch_date || data.expected_delivery || data.total_units || data.vgv_estimated) && (
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {data.launch_date && (
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Lançamento</div>
                  <div className="text-slate-700">{fmtDate(data.launch_date)}</div>
                </div>
              )}
              {data.expected_delivery && (
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Entrega prevista</div>
                  <div className="text-slate-700">{fmtDate(data.expected_delivery)}</div>
                </div>
              )}
              {data.total_units != null && (
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Unidades totais</div>
                  <div className="text-slate-700">{fmtNum(data.total_units)}</div>
                </div>
              )}
              {data.vgv_estimated != null && (
                <div>
                  <div className="text-[10px] uppercase text-slate-400">VGV estimado</div>
                  <div className="text-slate-700">{fmtBRL(data.vgv_estimated)}</div>
                </div>
              )}
            </div>
          )}
          {data.description && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 whitespace-pre-wrap">{data.description}</div>
          )}
          {data.highlights && Array.isArray(data.highlights) && data.highlights.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1.5">
              {data.highlights.map((h, i) => (
                <span key={i} className="px-2 py-0.5 bg-violet-50 text-violet-700 text-[11px] rounded">{h}</span>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Incorporadoras</h3>
            <span className="text-[11px] text-slate-400">({incorporadoras.length})</span>
          </div>
          {incorporadoras.length === 0 ? (
            <div className="text-xs text-slate-400">Nenhuma incorporadora vinculada.</div>
          ) : (
            <ul className="space-y-1.5">
              {incorporadoras.map((inc) => (
                <li key={inc.id} className="flex items-center gap-2 text-xs">
                  {inc.is_lead && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">LEAD</span>}
                  <span className="text-slate-700 font-medium">
                    {inc.empresa?.empresa || inc.empresa?.company_name || '—'}
                  </span>
                  <span className="text-[10px] text-slate-400 uppercase">{inc.role}</span>
                  {inc.participation_pct != null && (
                    <span className="text-[11px] text-slate-500">{fmtPct(inc.participation_pct)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <Ruler size={14} className="text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Tipologias</h3>
            <span className="text-[11px] text-slate-400">({tipologias.length})</span>
          </div>
          {tipologias.length === 0 ? (
            <div className="text-xs text-slate-400">Nenhuma tipologia cadastrada.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Tipo</th>
                    <th className="px-2 py-1.5 text-left">Label</th>
                    <th className="px-2 py-1.5 text-left">Uso</th>
                    <th className="px-2 py-1.5 text-left">Produto</th>
                    <th className="px-2 py-1.5 text-right">Dorms</th>
                    <th className="px-2 py-1.5 text-right">Área (m²)</th>
                    <th className="px-2 py-1.5 text-right">Suítes</th>
                    <th className="px-2 py-1.5 text-right">Vagas</th>
                    <th className="px-2 py-1.5 text-right">Unidades</th>
                    <th className="px-2 py-1.5 text-left">Indicadores</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tipologias.map((t) => {
                    const tipUseMeta = USE_META[t.use_category] ?? USE_META.unknown;
                    return (
                      <tr key={t.id}>
                        <td className="px-2 py-1.5 text-slate-700 font-medium">{t.tipo}</td>
                        <td className="px-2 py-1.5 text-slate-600">{t.label || '—'}</td>
                        <td className="px-2 py-1.5">
                          {t.use_category
                            ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tipUseMeta.color}`}>{tipUseMeta.label}</span>
                            : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{PRODUCT_LABEL[t.product_type] ?? t.product_type ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">{t.bedrooms ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">{fmtRange(t.area_privativa_min, t.area_privativa_max, fmtNum)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">{fmtNum(t.suites)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">{fmtNum(t.vagas)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">{fmtNum(t.total_units)}</td>
                        <td className="px-2 py-1.5">
                          <div className="flex flex-wrap gap-1">
                            {t.is_flexible_plan && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${FLEX_BADGE}`}>Flex</span>}
                            {t.needs_review && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${REVIEW_BADGE}`}>Revisar</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <UnidadesSection empreendimentoId={id} />

        <section className="bg-white border border-slate-200 rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={14} className="text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Preços vigentes</h3>
            <span className="text-[11px] text-slate-400">({precos.length})</span>
          </div>
          {precos.length === 0 ? (
            <div className="text-xs text-slate-400">Nenhum preço vigente cadastrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Tipologia</th>
                    <th className="px-2 py-1.5 text-right">R$/m²</th>
                    <th className="px-2 py-1.5 text-right">Preço total</th>
                    <th className="px-2 py-1.5 text-left">Vigência</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {precos.map((p) => (
                    <tr key={p.id}>
                      <td className="px-2 py-1.5 text-slate-700">
                        {p.tipologia ? `${p.tipologia.tipo}${p.tipologia.label ? ` — ${p.tipologia.label}` : ''}` : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right text-slate-700">
                        {fmtRange(p.price_per_m2_min, p.price_per_m2_max, (v) => fmtBRL(v))}
                      </td>
                      <td className="px-2 py-1.5 text-right text-slate-700">
                        {fmtRange(p.total_price_min, p.total_price_max, (v) => fmtBRL(v))}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500 text-[11px]">
                        desde {fmtDate(p.valid_from)}{p.valid_until ? ` até ${fmtDate(p.valid_until)}` : ' (vigente)'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={14} className="text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Materiais</h3>
            <span className="text-[11px] text-slate-400">({materials.length})</span>
          </div>
          {materials.length === 0 ? (
            <div className="text-xs text-slate-400">Nenhum material cadastrado.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {materials.map((m) => (
                <li key={m.id} className="py-1.5 flex items-center gap-2 text-xs">
                  {m.is_featured && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">★</span>}
                  <span className="text-slate-700 font-medium">{m.material_type}</span>
                  <span className="text-slate-500">{m.original_filename || '—'}</span>
                  {m.version_label && <span className="text-[10px] text-slate-400">v{m.version_label}</span>}
                  {m.source_url && (
                    <a href={m.source_url} target="_blank" rel="noreferrer" className="text-violet-700 hover:underline inline-flex items-center gap-0.5 ml-auto">
                      abrir <ExternalLink size={10} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <Layers size={14} className="text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Disponibilidade</h3>
          </div>
          <EmptyState
            message="Snapshots de disponibilidade ainda não cadastrados"
            hint="A ingestão de disponibilidade entra em sprint futura."
          />
        </section>

        <footer className="text-[11px] text-slate-400 flex items-center gap-2 border-t border-slate-100 pt-3">
          <Database size={11} />
          <span>
            source: <span className="text-slate-600">{data.source_type || '—'}</span>
            {data.source_reference && <> · ref: <span className="text-slate-600">{data.source_reference}</span></>}
            {data.last_verified_at && <> · verificado em <span className="text-slate-600">{fmtDate(data.last_verified_at)}</span></>}
            {data.source_confidence != null && <> · confiança {Math.round(Number(data.source_confidence) * 100)}%</>}
          </span>
        </footer>
      </div>
    </>
  );
}
